'use client';

import { useCallback, useRef, useState } from 'react';
import { getMaterialDetail } from '../api/getMaterialDetail';
import type { GfMaterialDetail } from '../types';

type UseMaterialDetailResult = {
  /** 現在表示中コマの詳細。未取得・取得失敗時は null */
  detail: GfMaterialDetail | null;
  /** 詳細フェッチ中か（利用可能商品ペインのローディング表示に使う） */
  isDetailLoading: boolean;
  /** 直近の詳細フェッチが失敗したか（利用可能商品ペインのエラー表示に使う） */
  isDetailError: boolean;
  /** 指定コマの詳細を読み込む。取得済みなら即時反映、未取得なら fetch。連続呼び出しは最後の 1 件だけ反映する */
  loadDetail: (materialId: number) => void;
  /** 取得済みの詳細を差し込む（開いた時点で詳細を持っているときに二重フェッチを避ける） */
  provideDetail: (material: GfMaterialDetail) => void;
  /** お気に入り状態だけを更新する（お気に入りボタン押下時の即時反映用） */
  updateFavoriteInCache: (materialId: number, isFavorite: boolean) => void;
};

/**
 * コマ詳細（利用可能商品など）の取得を担うフック。
 * 前後送りのたびに materialId で fetch するが、取得済みは Map にキャッシュして再取得しない。
 * 速い連続スワイプでは複数の fetch が並行するため、リクエスト連番で最後の要求だけを反映し、
 * 遅れて解決した古いコマの詳細で表示が入れ替わる取り違えを防ぐ。
 */
export function useMaterialDetail(): UseMaterialDetailResult {
  const [detail, setDetail] = useState<GfMaterialDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isDetailError, setIsDetailError] = useState(false);
  const cacheRef = useRef<Map<number, GfMaterialDetail>>(new Map());
  const latestRequestRef = useRef(0);

  const provideDetail = useCallback((material: GfMaterialDetail) => {
    cacheRef.current.set(material.id, material);
    latestRequestRef.current += 1; // 進行中のフェッチより優先させる
    setDetail(material);
    setIsDetailLoading(false);
    setIsDetailError(false);
  }, []);

  const loadDetail = useCallback((materialId: number) => {
    const cached = cacheRef.current.get(materialId);
    if (cached) {
      latestRequestRef.current += 1;
      setDetail(cached);
      setIsDetailLoading(false);
      setIsDetailError(false);
      return;
    }

    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    setIsDetailLoading(true);
    setIsDetailError(false);
    void (async () => {
      try {
        const { result } = await getMaterialDetail(materialId);
        cacheRef.current.set(materialId, result);
        // 自分より新しい要求が出ていたら破棄する（最新優先）
        if (requestId !== latestRequestRef.current) return;
        setDetail(result);
        setIsDetailLoading(false);
      } catch {
        if (requestId !== latestRequestRef.current) return;
        // 取得失敗時は古いコマの商品を出さないよう空にし、エラー表示に切り替える
        // （名前・画像は一覧データで表示済み。即オープン時はここが唯一の失敗通知経路になる）
        setDetail(null);
        setIsDetailLoading(false);
        setIsDetailError(true);
      }
    })();
  }, []);

  const updateFavoriteInCache = useCallback((materialId: number, isFavorite: boolean) => {
    const cached = cacheRef.current.get(materialId);
    if (cached) cacheRef.current.set(materialId, { ...cached, isFavorite });
    setDetail((prev) => (prev?.id === materialId ? { ...prev, isFavorite } : prev));
  }, []);

  return { detail, isDetailLoading, isDetailError, loadDetail, provideDetail, updateFavoriteInCache };
}
